import { c as _c } from "react/compiler-runtime";
import { hasObjectKey } from '@core/utils';
import type { QueryKey } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cloneDeep, debounce } from 'es-toolkit';
import { useRef } from 'react';
import { AuthApi, AuthQueryKey } from '@/api';
import { ROUTES } from '@/constants';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useAuthStore } from '@/stores/AuthStore';
import { isSameEndpointQuery } from '@/utils/isSameEndpointQuery';
import { useCustomRouter } from './useCustomRouter';
interface LikeParams {
  /**
   * 쿼리키 조회용
   */
  idx?: string;
  contentId: number;
  isLike: boolean;
  likeType: string;
}
const OPTIMISTIC_QUERY_KEYS = [AuthQueryKey['get:/wello/v2/auth/wello-comment'], AuthQueryKey['get:/wello/v2/auth/wello-comment/my']];
const ID_KEYS = ['communityId', 'welloCommentId', 'id'] as const;
const findCommentById = (obj: unknown, contentId: number): unknown => {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const idKey of ID_KEYS) {
    if (hasObjectKey(obj, idKey) && obj[idKey] === contentId) {
      return obj;
    }
  }
  if (Array.isArray(obj)) {
    for (const item of obj) {
      const found = findCommentById(item, contentId);
      if (found) return found;
    }
  } else {
    for (const key in obj) {
      if (hasObjectKey(obj, key)) {
        const found = findCommentById(obj[key], contentId);
        if (found) return found;
      }
    }
  }
  return undefined;
};
const updateLikeState = (comment: unknown, isLike: boolean) => {
  if (!comment || typeof comment !== 'object') return;
  if (hasObjectKey(comment, 'likeCount') && typeof comment.likeCount === 'number') {
    comment.likeCount = isLike ? comment.likeCount - 1 : comment.likeCount + 1;
  }
  if (hasObjectKey(comment, 'likeYN') && typeof comment.likeYN === 'boolean') {
    comment.likeYN = !isLike;
  }
};
const fixInfiniteQueryKey = (key: QueryKey): QueryKey => {
  const fixed = cloneDeep(key) as unknown[];
  if (fixed[0] && typeof fixed[0] === 'object' && hasObjectKey(fixed[0], 'query') && typeof fixed[0].query === 'object' && hasObjectKey(fixed[0].query, 'page')) {
    fixed[0].query.page = 1;
  }
  return fixed;
};
export const useLike = () => {
  const $ = _c(10);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    mutate
  } = useMutation(t0);
  const queryClient = useQueryClient();
  const router = useCustomRouter();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      type: MESSAGE_KEYWORD.HAPTIC
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    request: requestHaptic
  } = useHandleApp(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Map();
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const backupMapRef = useRef(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Map();
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const originLikeMapRef = useRef(t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = new Map();
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const debouncedMutateMapRef = useRef(t4);
  let t5;
  if ($[5] !== mutate || $[6] !== queryClient || $[7] !== requestHaptic || $[8] !== router) {
    const like = params => {
      const backupMap = backupMapRef.current;
      const originLikeMap = originLikeMapRef.current;
      const debouncedMutateMap = debouncedMutateMapRef.current;
      requestHaptic();
      const {
        contentId: contentId_0,
        isLike: isLike_0,
        idx
      } = params;
      const {
        isLogin
      } = useAuthStore.getState();
      if (!isLogin) {
        return router.push(ROUTES.LOGIN.withSearchParams({
          searchParams: {
            redirect: location.href
          }
        }));
      }
      if (!originLikeMap.has(contentId_0)) {
        originLikeMap.set(contentId_0, isLike_0);
      }
      const likeQueries = queryClient.getQueriesData({
        predicate: _temp2
      });
      likeQueries.forEach(t6 => {
        const [key, data] = t6;
        if ((key.includes(contentId_0) || idx && key.includes(idx)) && hasObjectKey(data, "context") && hasObjectKey(data.context, "likeCount") && hasObjectKey(data.context, "likeYN")) {
          const newData = cloneDeep(data);
          if (hasObjectKey(newData.context, "likeCount") && typeof newData.context.likeCount === "number" && hasObjectKey(newData.context, "likeYN") && typeof newData.context.likeYN === "boolean") {
            newData.context.likeCount = isLike_0 ? newData.context.likeCount - 1 : newData.context.likeCount + 1;
            newData.context.likeYN = !isLike_0;
            queryClient.setQueryData(key, newData);
          }
        } else {
          if (!backupMap.has(key)) {
            backupMap.set(key, cloneDeep(data));
          }
          if (!data) {
            return;
          }
          const newData_0 = cloneDeep(data);
          const comment = findCommentById(newData_0, contentId_0);
          if (comment) {
            updateLikeState(comment, isLike_0);
            queryClient.setQueryData(fixInfiniteQueryKey(key), newData_0);
          }
        }
      });
      if (!debouncedMutateMap.has(contentId_0)) {
        debouncedMutateMap.set(contentId_0, debounce((params_0, options) => {
          if (originLikeMap.get(contentId_0) === !params_0.isLike) {
            return;
          }
          return mutate(params_0, options);
        }, 500));
      }
      debouncedMutateMap.get(contentId_0)?.(params, {
        onError: () => {
          originLikeMap.delete(contentId_0);
          backupMap.forEach((data_0, key_0) => {
            queryClient.setQueryData(key_0, data_0);
          });
        },
        onSuccess: () => {
          backupMap.clear();
          originLikeMap.set(contentId_0, !params.isLike);
        }
      });
    };
    t5 = {
      like
    };
    $[5] = mutate;
    $[6] = queryClient;
    $[7] = requestHaptic;
    $[8] = router;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
function _temp(t0) {
  const {
    contentId,
    isLike,
    likeType
  } = t0;
  const request = isLike ? AuthApi["delete:/wello/v2/auth/wello-like"] : AuthApi["post:/wello/v2/auth/wello-like"];
  return request({
    body: {
      contentId,
      likeTypeCd: likeType
    }
  });
}
function _temp2(query) {
  return OPTIMISTIC_QUERY_KEYS.some(queryKey => isSameEndpointQuery(query, queryKey()));
}